import type { TourShow } from '../domain/models'
import { publicUrl } from '../routing/basePath'

interface AbstractFitMediaFixture {
  id: string
  kind: 'abstract'
  label: string
  postId: string
  variant: 'crossed-signal' | 'torn-grid'
}

interface PhotoFitMediaFixture {
  alt: string
  assetId: string
  height: number
  id: string
  kind: 'photo'
  objectPosition: string
  postId: string
  src: string
  srcSet: string
  width: number
}

export type FitMediaFixture = AbstractFitMediaFixture | PhotoFitMediaFixture

export const fitMediaFixtures: readonly FitMediaFixture[] = [
  {
    id: 'fit-band-photo-01',
    kind: 'photo',
    postId: 'seed-fits-01',
    assetId: 'the-paradox-official-band-hero-local-review-100826',
    alt: 'The four members of The Paradox posing together in a wood-paneled room, wearing black, blue, white, and pink casual outfits.',
    src: publicUrl('assets/the-paradox-band-hero-960.jpg'),
    srcSet: `${publicUrl('assets/the-paradox-band-hero-960.jpg')} 960w, ${publicUrl('assets/the-paradox-band-hero-1440.jpg')} 1440w`,
    objectPosition: '58% 52%',
    width: 960,
    height: 540,
  },
  {
    id: 'fit-abstract-02',
    kind: 'abstract',
    label: 'Abstract off-white grid cut by black and red torn-paper shapes.',
    postId: 'seed-fits-02',
    variant: 'torn-grid',
  },
] as const

export interface SceneFixtureState {
  label: string
  status: 'sold-out' | 'low-tickets' | 'unposted'
}

export type SceneTicketStatus = SceneFixtureState['status']

export interface SceneCrowdVisual {
  alt: string
  crowdLevel: 'building' | 'light' | 'packed'
  height: 1024
  src: string
  srcSet: string
  width: 1536
}

export const sceneCrowdVisualByStatus = {
  'sold-out': {
    alt: 'Illustrated packed punk crowd facing The Paradox as the band performs in an open garage at night.',
    crowdLevel: 'packed',
    height: 1024,
    src: publicUrl('assets/the-paradox-crowd-packed-960.webp'),
    srcSet: `${publicUrl('assets/the-paradox-crowd-packed-960.webp')} 960w, ${publicUrl('assets/the-paradox-crowd-packed-1536.webp')} 1536w`,
    width: 1536,
  },
  'low-tickets': {
    alt: 'Illustrated building punk crowd facing The Paradox as the band performs in an open garage at night.',
    crowdLevel: 'building',
    height: 1024,
    src: publicUrl('assets/the-paradox-crowd-building-960.webp'),
    srcSet: `${publicUrl('assets/the-paradox-crowd-building-960.webp')} 960w, ${publicUrl('assets/the-paradox-crowd-building-1536.webp')} 1536w`,
    width: 1536,
  },
  unposted: {
    alt: 'Illustrated light punk crowd facing The Paradox as the band performs in an open garage at night.',
    crowdLevel: 'light',
    height: 1024,
    src: publicUrl('assets/the-paradox-crowd-light-960.webp'),
    srcSet: `${publicUrl('assets/the-paradox-crowd-light-960.webp')} 960w, ${publicUrl('assets/the-paradox-crowd-light-1536.webp')} 1536w`,
    width: 1536,
  },
} as const satisfies Record<SceneTicketStatus, SceneCrowdVisual>

export function resolveSceneCrowdVisual(
  state: Pick<SceneFixtureState, 'status'>,
): SceneCrowdVisual {
  return sceneCrowdVisualByStatus[state.status]
}

export interface TourTicketStatus {
  city: string
  dateLabel: string
  region: string
  status: SceneTicketStatus
}

export const tourTicketStatuses: readonly TourTicketStatus[] = [
  { city: 'Dallas', region: 'TX', dateLabel: 'OCT 14', status: 'sold-out' },
  { city: 'Houston', region: 'TX', dateLabel: 'OCT 15', status: 'sold-out' },
  { city: 'Austin', region: 'TX', dateLabel: 'OCT 16', status: 'sold-out' },
  { city: 'Phoenix', region: 'AZ', dateLabel: 'OCT 19', status: 'sold-out' },
  { city: 'San Diego', region: 'CA', dateLabel: 'OCT 20', status: 'sold-out' },
  { city: 'Los Angeles', region: 'CA', dateLabel: 'OCT 22', status: 'sold-out' },
  { city: 'Los Angeles', region: 'CA', dateLabel: 'OCT 23', status: 'sold-out' },
  { city: 'Salt Lake City', region: 'UT', dateLabel: 'OCT 25', status: 'sold-out' },
  { city: 'Denver', region: 'CO', dateLabel: 'OCT 27', status: 'sold-out' },
  { city: 'St. Louis', region: 'MO', dateLabel: 'OCT 30', status: 'sold-out' },
  { city: 'Milwaukee', region: 'WI', dateLabel: 'OCT 31', status: 'unposted' },
  { city: 'Indianapolis', region: 'IN', dateLabel: 'NOV 02', status: 'low-tickets' },
  { city: 'Detroit', region: 'MI', dateLabel: 'NOV 03', status: 'low-tickets' },
  { city: 'Columbus', region: 'OH', dateLabel: 'NOV 04', status: 'sold-out' },
  { city: 'Boston', region: 'MA', dateLabel: 'NOV 06', status: 'sold-out' },
  { city: 'New York', region: 'NY', dateLabel: 'NOV 08', status: 'sold-out' },
  { city: 'New York', region: 'NY', dateLabel: 'NOV 09', status: 'sold-out' },
  { city: 'Philadelphia', region: 'PA', dateLabel: 'NOV 11', status: 'sold-out' },
  { city: 'Towson', region: 'MD', dateLabel: 'NOV 13', status: 'sold-out' },
  { city: 'Jacksonville', region: 'FL', dateLabel: 'NOV 17', status: 'sold-out' },
  { city: 'Atlanta', region: 'GA', dateLabel: 'NOV 18', status: 'sold-out' },
] as const

export const defaultSceneFixtureState: SceneFixtureState = {
  label: 'No update yet',
  status: 'unposted',
}

export function resolveSceneFixtureState(
  show: Pick<TourShow, 'city' | 'dateLabel' | 'isoDate' | 'region'>,
): SceneFixtureState {
  const year = new Date(show.isoDate).getFullYear()
  if (year !== 2026) return defaultSceneFixtureState
  const ticketStatus = tourTicketStatuses.find((item) => (
    item.city === show.city
    && item.region === show.region
    && item.dateLabel === show.dateLabel
  ))
  if (!ticketStatus) return defaultSceneFixtureState
  if (ticketStatus.status === 'sold-out') {
    return {
      label: 'Sold out',
      status: 'sold-out',
    }
  }
  if (ticketStatus.status === 'low-tickets') {
    return {
      label: 'Only a few tickets left',
      status: 'low-tickets',
    }
  }
  return {
    label: 'No update yet',
    status: 'unposted',
  }
}
